/**
 * Form logic of the profile's manual repairs (contract #5, task T.C3 / И4).
 *
 * Pure on purpose (unit-tested without a DOM): every function takes the row the
 * server sent and the draft the manager typed, and answers with the PATCH body —
 * or the error to show instead. The invariants mirrored here are the api's own:
 *
 *   * PII (`fullName`/`phone`/`email`) is writable ONLY while the field is null —
 *     a sourced field is source-owned and any write answers 403 FORBIDDEN, so the
 *     UI disables it instead of sending a body the api must refuse;
 *   * `phone` must be the canonical `+7(XXX)XXXXXXX`, `email` is lowercased,
 *     `fullName` is trimmed;
 *   * `venueId` is a positive integer (T.Z16: picked from the venue directory, so
 *     a value is always a directory id), or `null` to unlink (back to UNMATCHED) —
 *     and `null` travels only from «Убрать площадку», never from a combobox clear;
 *   * a body that would change nothing is a no-op — the UI disables the submit.
 */

import type { ApplicationDto, ApplicationPatchInput } from '../../api/applications';
import type { Role } from '../../auth/roles';
import { hasAtLeast } from '../../auth/roles';
// Type-only: the unit project runs in plain Node, so a value import of a
// component module would drag React into it (the same rule quotas-model states).
import type { ComboboxOption } from '../../components/combobox-model';

export type PiiField = 'fullName' | 'phone' | 'email';
/** The manual flags a MANAGER+ may flip — ADDITIVE (v1.9.15, T.Z96) added the third. */
export type FlagField = 'isOrganic' | 'agreementRejected' | 'eviFailedManually';

/** Canonical ingest shape `+7(XXX)XXXXXXX` (docs/05 §3) — the api's own rule. */
export const PHONE_PATTERN = /^\+7\(\d{3}\)\d{7}$/;
/** Same shape the api accepts; it stores the value lowercased. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The three draft inputs of the null-fill form (always strings, '' = leave empty). */
export interface PiiFillDraft {
  fullName: string;
  phone: string;
  email: string;
}

export const emptyPiiFillDraft = (): PiiFillDraft => ({ fullName: '', phone: '', email: '' });

export interface PiiFillFieldState {
  field: PiiField;
  /** `false` = the field is source-owned: rendered read-only, never sent. */
  editable: boolean;
  /** The draft, trimmed. */
  value: string;
  /** `undefined` when the draft may go out as it is. */
  error: string | undefined;
  /** `true` when the field is part of the PATCH body. */
  included: boolean;
}

function validatePii(field: PiiField, raw: string): string | undefined {
  const value = raw.trim();
  if (value === '') return undefined;
  if (field === 'phone') {
    return PHONE_PATTERN.test(value) ? undefined : 'Телефон в формате +7(XXX)XXXXXXX';
  }
  if (field === 'email') {
    return EMAIL_PATTERN.test(value.toLowerCase()) ? undefined : 'Некорректный email';
  }
  return undefined;
}

/**
 * State of one PII input. A field the snapshot already filled is NOT editable —
 * there is no "clear" affordance either, because clearing a sourced field is a 403.
 */
export function piiFillState(
  field: PiiField,
  draft: PiiFillDraft,
  application: ApplicationDto,
): PiiFillFieldState {
  const value = (draft[field] ?? '').trim();
  const editable = application[field] === null;
  const error = editable ? validatePii(field, value) : undefined;
  return { field, editable, value, error, included: editable && value !== '' && error === undefined };
}

export interface PiiFillResult {
  patch: ApplicationPatchInput;
  /** One message per invalid field, keyed by field. */
  errors: Partial<Record<PiiField, string>>;
  /** `true` when the body would change something and no field is invalid. */
  canSubmit: boolean;
  /** Fields with a value already on the row — the reason the inputs are off. */
  sourceOwned: PiiField[];
}

/** The PATCH body of the null-fill form: only filled, valid, still-null fields. */
export function piiFillPatch(draft: PiiFillDraft, application: ApplicationDto): PiiFillResult {
  const patch: ApplicationPatchInput = {};
  const errors: Partial<Record<PiiField, string>> = {};
  const sourceOwned: PiiField[] = [];

  for (const field of ['fullName', 'phone', 'email'] as const) {
    const state = piiFillState(field, draft, application);
    if (!state.editable) {
      sourceOwned.push(field);
      continue;
    }
    if (state.error !== undefined) {
      errors[field] = state.error;
      continue;
    }
    if (state.value === '') continue;
    if (field === 'email') patch.email = state.value.toLowerCase();
    else patch[field] = state.value;
  }

  const hasErrors = Object.keys(errors).length > 0;
  // One invalid field blocks the WHOLE submit: a body that silently drops the
  // typo'd field while sending the rest would fill half of what the manager typed.
  return {
    patch: hasErrors ? {} : patch,
    errors,
    canSubmit: !hasErrors && Object.keys(patch).length > 0,
    sourceOwned,
  };
}

/** The body that flips one manual flag (a no-op is the caller's business). */
export function flagTogglePatch(application: ApplicationDto, field: FlagField): ApplicationPatchInput {
  return { [field]: !application[field] } as ApplicationPatchInput;
}

/** `''` = no selection yet (a combobox state, not a write), else a positive integer. */
export function venueAssignError(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed)) return 'ID площадки — число';
  const parsed = Number.parseInt(trimmed, 10);
  return parsed >= 1 ? undefined : 'ID площадки — число больше нуля';
}

export interface VenueAssignResult {
  patch: ApplicationPatchInput;
  error: string | undefined;
  /** `false` when the id equals the current one — the api would answer a no-op. */
  changed: boolean;
  canSubmit: boolean;
}

/**
 * The body of the manual venue control's «Назначить» (T.Z16).
 *
 * The value a combobox hands over is a directory id — the dictionary is the ONLY
 * way to name a venue now, so a manager can never send an id the api would
 * reject for its shape. `''` is "nothing selected yet": a no-op, NOT an unlink —
 * unlinking is the separate destructive «Убрать площадку» action
 * ({@link venueRemovePatch}), because a combobox's × must never carry the weight
 * of removing an attribution.
 */
export function venueAssignPatch(application: ApplicationDto, value: string): VenueAssignResult {
  const error = venueAssignError(value);
  if (error !== undefined) return { patch: {}, error, changed: false, canSubmit: false };
  const trimmed = value.trim();
  if (trimmed === '') return { patch: {}, error: undefined, changed: false, canSubmit: false };
  const venueId = Number.parseInt(trimmed, 10);
  const changed = venueId !== application.venueId;
  return { patch: changed ? { venueId } : {}, error: undefined, changed, canSubmit: changed };
}

/** The body of «Убрать площадку» (T.Z16): the contract's `venueId: null`. */
export function venueRemovePatch(application: Pick<ApplicationDto, 'venueId'>): VenueAssignResult {
  const changed = application.venueId !== null;
  return { patch: changed ? { venueId: null } : {}, error: undefined, changed, canSubmit: changed };
}

// ---------------------------------------------------------------------------
// ADDITIVE wave 9 (v1.8.0, T.Y9) — the venue-change guard (Р7 v2.1 / A8, D1)
// ---------------------------------------------------------------------------

/**
 * The hint the DISABLED offline control carries once the row HAS a venue. The
 * field stays VISIBLE on purpose (the SR is explicit: «задизейблено для офлайн,
 * не скрыто») — a manager must see that the venue exists and that the silence of
 * the controls is a rule, not a rendering bug. No format at all reads as the
 * offline world (the api's own reading).
 */
export const VENUE_OFFLINE_HINT = 'Смена площадки для офлайн-заявлений закрыта';

/**
 * ADDITIVE (A8 amended 14.09, v1.8.8) — the hint a MANAGER sees on an UNASSIGNED
 * offline row: the row can still receive its first venue, and the decision is the
 * ADMIN's.
 */
export const VENUE_OFFLINE_FIRST_HINT = 'Первое назначение площадки для офлайн-заявления выполняет администратор.';

/** The hint a MANAGER sees where the ADMIN's controls would be, on an ONLINE row. */
export const VENUE_ROLE_HINT = 'Смену площадки для онлайн-заявления выполняет администратор.';

/** What the «Площадка» row of the repair panel can be (SR §5.3). */
export type VenueControlState =
  /**
   * Disabled with {@link VENUE_OFFLINE_HINT} — an offline row that already HAS a
   * venue: assigned once, never moved again (A8 amended 14.09), every role.
   */
  | 'offline-locked'
  /**
   * Read-only for this role (T.Z16: the combobox renders DISABLED, so a manager
   * still sees WHICH venue the row carries), plus {@link VENUE_ROLE_HINT} — or,
   * since A8 amended 14.09, {@link VENUE_OFFLINE_FIRST_HINT} on an unassigned
   * offline row.
   */
  | 'admin-only'
  /** The ADMIN's editable control, with the confirmation step before the PATCH. */
  | 'editable';

/**
 * Which of the three states a row is in. A8 AMENDED 14.09 (v1.8.8): an offline
 * row (or one with no format) receives a venue ONCE — an UNASSIGNED one shows the
 * ADMIN the same editable control an online row gets (with the confirmation step),
 * while a non-ADMIN reads the disabled control with the role hint; a row that
 * already HAS a venue never moves again, for any role. An ONLINE row moves only by
 * an ADMIN decision in every case. The api enforces the same rule with distinct
 * 403s (`VENUE_CHANGE_OFFLINE_FORBIDDEN` / `VENUE_REASSIGN_DISABLED`); the UI just
 * avoids sending what would be refused.
 *
 * RE-ANCHORED (v1.9.93, T.Z245): «offline vs online» is the SERVED `format` — the
 * EFFECTIVE УНТИ reading, now the api's own rule axis (the guard reads the same
 * `effectiveFormatOf` projection #5 renders). The T.Z244 detour through
 * `storedFormat` is superseded: the stored cell (still on the wire, v1.9.91) is
 * provenance, not the gate — a stored-offline child УНТИ moved to an online course
 * MUST render movable.
 */
export function venueControlState(
  application: Pick<ApplicationDto, 'format' | 'venueId'>,
  role: Role | null | undefined,
): VenueControlState {
  if (application.format !== 'ONLINE') {
    // The one-time rule: an assigned offline row is locked for good; an unassigned
    // one takes the same role split an online row does — the ADMIN's decision.
    if (application.venueId !== null) return 'offline-locked';
    return hasAtLeast(role, 'ADMIN') ? 'editable' : 'admin-only';
  }
  // An absent role is never at least ADMIN — the same fail-closed rule roles.ts runs on.
  return hasAtLeast(role, 'ADMIN') ? 'editable' : 'admin-only';
}

// ---------------------------------------------------------------------------
// ADDITIVE wave 10 (T.Z16) — the venue control speaks names, not ids
// ---------------------------------------------------------------------------

/** What the combobox of the venue row is searched over (#6, the venue directory). */
export interface VenueAssignOptionEntry {
  id: number;
  name: string;
  address?: string | null;
  region?: string | null;
}

/**
 * The combobox rows of the venue control (T.Z16): the venue NAME is the primary
 * line and the REGION the secondary, searchable one — the owner reads venues by
 * «название, регион», and an address is only the fallback when a row carries no
 * region at all. Same shape as the filter bar's options, different secondary
 * text (there it is the address, the only field the quota page had).
 *
 * Pure, so the unit project pins the ordering and the fallback without a DOM.
 */
export function venueAssignOptions(entries: readonly VenueAssignOptionEntry[]): ComboboxOption[] {
  return entries.map((entry) => ({
    value: String(entry.id),
    label: entry.name,
    hint: entry.region ?? entry.address ?? undefined,
  }));
}

/**
 * The venue a directory does not list any more, re-attached as a single option:
 * the row's own `venueName` must stay readable even when #6 no longer knows the
 * id (renamed, retired). Only ever INJECTED for the current id — it must not add
 * a venue the dictionary genuinely does not have to the searchable list.
 */
export function venueAssignFallbackOption(
  application: Pick<ApplicationDto, 'venueId' | 'venueName'>,
): ComboboxOption[] {
  if (application.venueId === null || application.venueName === null) return [];
  return [{ value: String(application.venueId), label: application.venueName }];
}

/** «Без площадки», where «—» would read as a rendering hole (T.Z16). */
export const VENUE_EMPTY_LABEL = 'Без площадки';

/** The link-breaking action, named for what it does (the destructive half). */
export const VENUE_REMOVE_LABEL = 'Убрать площадку';
/** The armed state of that action — names WHICH write the second click sends. */
export const VENUE_REMOVE_CONFIRM_LABEL = 'Подтвердить снятие';

/** The assign action, and its armed state (the existing confirm gate, T.Y9). */
export const VENUE_ASSIGN_LABEL = 'Назначить';
export const VENUE_ASSIGN_CONFIRM_LABEL = 'Подтвердить смену';

/** Which of the two writes an armed confirm button would send. */
export type VenueConfirmIntent = 'assign' | 'remove';
